import React from "react";
import { Bot, Camera, ScanLine, Boxes, ArrowRight } from "lucide-react";

import { SplineScene } from "../ui/splite";
import { ErrorBoundary } from "../ui/error-boundary";

const RobotFallback = () => (
  <div className="flex h-full w-full items-center justify-center">
    <div className="relative">
      <div className="absolute inset-0 rounded-full bg-brand-orchid/30 blur-3xl" aria-hidden />
      <div className="relative flex h-44 w-44 items-center justify-center rounded-full border border-line bg-white shadow-xl shadow-brand-plum/5">
        <Bot className="h-20 w-20 text-brand-magenta" strokeWidth={1.2} />
      </div>
    </div>
  </div>
);

const AREAS = [
  {
    icon: Bot,
    title: "Automated assessment station",
    body: "Repeatable device testing with less manual handling.",
  },
  {
    icon: Camera,
    title: "Camera-assisted grading",
    body: "Consistent device imaging to support cosmetic condition review.",
  },
  {
    icon: ScanLine,
    title: "Auto MDM check station",
    body: "Automated ownership, MDM, and lock-risk workflows.",
  },
  {
    icon: Boxes,
    title: "Trade-in robot concepts",
    body: "Customer-facing and warehouse-based trade-in automation.",
  },
];

const preselectRobot = () => {
  window.dispatchEvent(new CustomEvent("furloop:preselect", { detail: "robot" }));
};

const RobotRnD = () => {
  return (
    <section
      id="automation"
      className="relative overflow-hidden bg-gradient-to-b from-brand-wash/60 via-brand-bg to-brand-bg font-body"
    >
      {/* atmosphere */}
      <div className="bg-dot-grid pointer-events-none absolute inset-0 opacity-60" aria-hidden />
      <div
        className="pointer-events-none absolute -left-24 top-0 h-[28rem] w-[28rem] rounded-full bg-brand-orchid/25 blur-3xl"
        aria-hidden
      />
      {/* soft halo behind the robot's head — kept high so its base sits on clean lavender */}
      <div
        className="pointer-events-none absolute right-0 top-12 h-[24rem] w-[24rem] rounded-full bg-brand-orchid/20 blur-3xl"
        aria-hidden
      />

      <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-6 py-20 lg:grid-cols-2 lg:py-28">
        {/* copy */}
        <div>
          <span className="inline-flex items-center gap-2 rounded-full border border-line bg-white px-3.5 py-1.5 text-xs font-medium uppercase tracking-[0.16em] text-brand-plum">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-magenta" />
            Automation R&D
          </span>
          <h2 className="mt-5 font-display text-3xl font-semibold leading-tight tracking-tight text-brand-plum sm:text-4xl lg:text-5xl">
            Robot Automation for IT Asset Recovery
          </h2>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-ink sm:text-lg">
            Furloop is researching automation hardware to reduce manual testing, grading, and processing work
            for high-volume recovery teams.
          </p>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-muted">
            Register interest to join the early design partner list for Furloop's robot automation R&D
            program.
          </p>

          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            {AREAS.map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                className="rounded-2xl border border-line bg-white p-5 transition-all hover:-translate-y-1 hover:border-brand-orchid hover:shadow-xl hover:shadow-brand-plum/5"
              >
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-brand-wash text-brand-magenta ring-1 ring-brand-orchid/30">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-3 font-display text-base font-semibold text-brand-plum">{title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted">{body}</p>
              </div>
            ))}
          </div>

          <a
            href="#register"
            onClick={preselectRobot}
            className="group mt-8 inline-flex items-center gap-2 rounded-full bg-cta-gradient px-6 py-3 text-sm font-semibold text-white no-underline shadow-lg shadow-[#304BEF]/30 transition-transform duration-200 hover:-translate-y-0.5 hover:no-underline"
          >
            Register Interest in Robot Automation
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
          </a>
        </div>

        {/* 3D robot */}
        <div className="relative h-[360px] w-full sm:h-[460px] lg:h-[540px]">
          <ErrorBoundary fallback={<RobotFallback />}>
            <SplineScene
              scene="https://prod.spline.design/kZDDjO5HuC9GJUM2/scene.splinecode"
              className="h-full w-full"
            />
          </ErrorBoundary>
          {/* dissolve the robot's base into the light-lavender page background */}
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-2/5 bg-gradient-to-t from-brand-bg via-brand-bg/85 to-transparent"
            aria-hidden
          />
        </div>
      </div>
    </section>
  );
};

export default RobotRnD;
